import { useState } from 'react';
import { reportsApi } from '../services/api/endpoints/reports';
import { 
  mockBalanceSheet, 
  mockIncomeStatement, 
  mockCashFlow, 
  mockEquityChanges,
  mockNotes 
} from '../data/mockFinancialData';
import type { ReportGenerationParams } from '../types/reporting-filters';
import { useCurrency } from './useCurrency';

// Helper to convert all numerical values in an object
const convertObjectValues = (obj: any, convert: (amount: number) => number): any => {
    if (!obj) return obj;
    if (typeof obj !== 'object') return obj;

    if (Array.isArray(obj)) {
        return obj.map(item => convertObjectValues(item, convert));
    }

    const newObj: { [key: string]: any } = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (typeof value === 'number' && key !== 'code' && key !== 'variation') { // Don't convert codes/percentages
                newObj[key] = convert(value);
            } else if (typeof value === 'object') {
                newObj[key] = convertObjectValues(value, convert);
            } else {
                newObj[key] = value;
            }
        }
    }
    return newObj;
};

export function useReports() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { convertAmount, baseCurrency } = useCurrency();

  const generateReport = async (params: ReportGenerationParams) => {
    setLoading(true);
    setError(null);

    try {
      // Try API first if online
      if (navigator.onLine) {
        try {
          // Pour les formats PDF/Excel, utiliser l'API de génération
          if (params.format === 'pdf' || params.format === 'excel') {
            const response = await reportsApi.generateReport(params);
            if (response.success && response.data) {
              // Return blob for download
              setData(response.data);
              return;
            }
          } else {
            // Pour le format JSON, utiliser les endpoints spécifiques
            let apiResponse;
            switch (params.type) {
              case 'balance':
                apiResponse = await reportsApi.getBalanceSheet({
                  date: params.date || new Date().toISOString().split('T')[0],
                  comparative: params.comparative,
                  currency: params.currency
                });
                break;
              case 'income':
                apiResponse = await reportsApi.getIncomeStatement({
                  startDate: params.startDate || new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
                  endDate: params.endDate || new Date().toISOString().split('T')[0],
                  comparative: params.comparative,
                  currency: params.currency
                });
                break;
              case 'cashflow':
                apiResponse = await reportsApi.getCashFlowStatement({
                  startDate: params.startDate || new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
                  endDate: params.endDate || new Date().toISOString().split('T')[0],
                  comparative: params.comparative,
                  currency: params.currency
                });
                break;
              case 'equity-changes':
                apiResponse = await reportsApi.getEquityChanges({
                  startDate: params.startDate || new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
                  endDate: params.endDate || new Date().toISOString().split('T')[0],
                  currency: params.currency
                });
                break;
              case 'notes':
                apiResponse = await reportsApi.getNotes({
                  date: params.date || new Date().toISOString().split('T')[0],
                  type: 'all'
                });
                break;
              default:
                throw new Error(`Report type ${params.type} not supported by API`);
            }
            
            if (apiResponse?.success && apiResponse.data) {
              setData(apiResponse.data);
              return;
            }
          }
        } catch (apiError) {
          console.error('API call failed, falling back to mock data:', apiError);
        }
      }

      // Fallback to mock data with simulated delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Utiliser les données de démonstration selon le type
      let mockData;
      switch (params.type) {
        case 'balance':
          mockData = mockBalanceSheet;
          break;
        case 'income':
          mockData = mockIncomeStatement;
          break;
        case 'cashflow':
          mockData = mockCashFlow;
          break;
        case 'equity-changes':
          mockData = mockEquityChanges;
          break;
        case 'notes':
          mockData = mockNotes;
          break;
        default:
          mockData = null;
      }

      // Convertir les montants si une devise est spécifiée et différente de la devise de base
      if (params.currency && params.currency !== baseCurrency && mockData) {
        const convertedData = convertObjectValues(mockData, (amount) => 
            convertAmount(amount, baseCurrency, params.currency!)
        );
        setData(convertedData);
      } else {
        setData(mockData);
      }

    } catch (err) {
      console.error('Error generating report:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    generateReport,
    exportReport: async (type: any, format: 'pdf' | 'excel', reportData: any) => {
      try {
        setLoading(true);
        const response = await reportsApi.exportReport({
          type,
          format,
          data: reportData
        });
        if (response.success && response.data) {
          // Create download link for blob
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${type}-report.${format}`;
          link.click();
          window.URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error('Error exporting report:', err);
        setError(err instanceof Error ? err.message : 'Erreur lors de l\'export');
      } finally {
        setLoading(false);
      }
    },
    searchReports: async (searchParams: any) => {
      try {
        setLoading(true);
        const response = await reportsApi.searchReports(searchParams);
        return response.success ? response.data : [];
      } catch (err) {
        console.error('Error searching reports:', err);
        return [];
      } finally {
        setLoading(false);
      }
    }
  };
}